import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pickApi, reportsApi, type Group, type Pick, type Row } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { Combobox } from "../ui/Combobox";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";
import { ALL_PAGES } from "../shell/nav";
import { findReport, type Col, type Param, type ReportDef } from "./registry";
import { iso, presets, quarterOf, taxYearOf, toCsv } from "./period";
import { toUrl } from "../shell/routes";
import { xlsx } from "../lib/xlsx";
import { ScheduleDialog } from "./Schedules";

const fmtQty = (n: number) => n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
export function cell(c: Col, v: unknown): string {
  if (v == null || v === "") return "";
  switch (c.kind) {
    case "amount": return fmtAmount(Number(v));
    case "signed": { const n = Number(v); return n === 0 ? "0.00" : `${fmtAmount(Math.abs(n))} ${n > 0 ? "Dr" : "Cr"}`; }
    case "qty": return fmtQty(Number(v));
    case "pct": return `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
    case "date": return fmtDate(String(v));
    default: return String(v);
  }
}
const numeric = (c: Col) => c.kind === "amount" || c.kind === "signed" || c.kind === "qty" || c.kind === "int" || c.kind === "pct";

// /…/r/:name — any report in the registry; its parameters live in the address, so drilling down and back keeps them
export function ReportPage({ name: fixed }: { name?: string }) {
  const { slug } = useParams();
  const routeName = slug?.replace(/-/g, "_");
  const def = findReport(fixed ?? routeName ?? "");
  if (!def) return <section className="page"><PageHead title="Report" /><ErrorNote text="There's no such report." /></section>;
  return <Report key={def.name} def={def} />;
}

function Report({ def }: { def: ReportDef }) {
  const { company, ref } = useCompany();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const years = useLoad(() => reportsApi.years(company.id), [company.id]);
  const today = iso(new Date());
  const ps = useMemo(() => presets(years.data ?? [], today), [years.data, today]);

  // defaults for whatever the address doesn't say
  const params: Record<string, string> = Object.fromEntries(sp.entries());
  const has = (p: Param) => def.params.includes(p);
  if (has("range") && !params.from && ps[0]) { params.from = ps[0].from; params.to = ps[0].to; }
  if (has("as_on") && !params.as_on) { const last = years.data?.at(-1)?.ends_on; params.as_on = last && last < today ? last : today; }
  if (has("period") && !params.period) { const t = new Date(); params.period = iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)); }
  if (has("tax_year") && !params.tax_year) params.tax_year = String(taxYearOf(today));
  if (has("statement")) { params.form ??= "140"; params.tax_year ??= String(taxYearOf(today)); params.quarter ??= String(quarterOf(today)); }
  if (has("registration") && !params.registration && ref?.gst_registrations?.[0]) params.registration = ref.gst_registrations[0].id;
  if (has("basis")) params.basis ??= "due";
  const set = (patch: Record<string, string | null>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries({ ...params, ...patch })) { if (v == null || v === "") n.delete(k); else n.set(k, v); }
    setSp(n, { replace: true });
  };

  const missing = (has("ledger") && !params.ledger) || (has("bank") && !params.ledger) || (has("variant") && !params.variant) || (has("registration") && !params.registration)
    || (has("range") && !params.from);
  const key = JSON.stringify(params);
  const { data: raw, error, loading } = useLoad(async () => (missing ? null : reportsApi.run(company.id, def.name, {
    ...params, tax_year: params.tax_year ? Number(params.tax_year) : undefined, quarter: params.quarter ? Number(params.quarter) : undefined,
  })), [company.id, def.name, key, missing]);

  // a trust's statements are an Income and Expenditure Account; the same figures can be run as a Profit and Loss
  const ieOn = !!company.income_expenditure && def.name in PL_TITLES;
  const asPl = ieOn && params.view === "pl";
  const data = useMemo(() => (raw && asPl ? raw.map((r) => (typeof r.label === "string" && PL_WORDS[r.label] ? { ...r, label: PL_WORDS[r.label] } : r)) : raw), [raw, asPl]);

  const exportCsv = () => {
    if (!data) return;
    const blob = new Blob([toCsv(def.columns, data)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${def.name}-${params.from ?? params.as_on ?? params.period ?? ""}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const open = (r: Row) => { const path = def.drill?.(r, params); if (path) nav(toUrl(`${path}`)); };
  const title = asPl ? PL_TITLES[def.name] : ALL_PAGES.find((p) => p.path.endsWith(`/r/${def.name}`))?.title ?? def.title;
  const when = params.from ? `${fmtDate(params.from)} to ${fmtDate(params.to)}` : params.as_on ? `as at ${fmtDate(params.as_on)}` : "";
  const fileBase = `${def.name}-${params.from ?? params.as_on ?? params.period ?? params.tax_year ?? ""}`;
  const save = (blob: Blob, name: string) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000); };
  const exportXlsx = () => {
    if (!data) return;
    const kind = (c: Col) => (c.kind === "date" ? "date" : c.kind && c.kind !== "text" && c.kind !== "id" ? "number" : "text") as "date" | "number" | "text";
    const bytes = xlsx(title, def.columns.map((c) => ({ label: c.label || " ", kind: kind(c) })), data.map((r) => def.columns.map((c) => r[c.key] as string | number | null)),
      `${company.name} · ${title}${when ? ` · ${when}` : ""}`);
    save(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${fileBase}.xlsx`);
  };
  const body = useRef<HTMLDivElement>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    if (!body.current) return;
    setPdfBusy(true);
    try {
      const { elementToPdf } = await import("../messages/pdf");
      body.current.classList.add("pdf-capture");
      const b64 = await elementToPdf(body.current, "A4");
      const bin = atob(b64); const u = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
      save(new Blob([u], { type: "application/pdf" }), `${fileBase}.pdf`);
    } finally { body.current?.classList.remove("pdf-capture"); setPdfBusy(false); }
  };
  const [scheduling, setScheduling] = useState(false);

  return (
    <section className="page report">
      <PageHead crumb={def.section === "reports" ? "Reports" : def.section.toUpperCase().replace("TDS", "TDS / TCS").replace("MSME", "Micro and small enterprises")} title={title}>
        {data && data.length > 0 && <><button onClick={exportXlsx}>Excel</button><button onClick={exportCsv}>CSV</button>
          <button disabled={pdfBusy} onClick={() => void exportPdf()}>{pdfBusy ? "PDF…" : "PDF"}</button><button onClick={() => window.print()}>Print</button></>}
        <button className="link" onClick={() => setScheduling(true)}>Email regularly…</button>
      </PageHead>
      {def.note && <p className="muted small">{def.note}</p>}
      {ieOn && <div className="view-switch" role="group" aria-label="Show as">
        <button type="button" aria-pressed={!asPl} onClick={() => set({ view: null })}>Income and Expenditure</button>
        <button type="button" aria-pressed={asPl} onClick={() => set({ view: "pl" })}>Profit and Loss</button></div>}
      <Params def={def} params={params} set={set} presets={ps} />
      {scheduling && <ScheduleDialog report={def.name} title={title} params={params} needsRange={has("range")} onClose={() => setScheduling(false)} />}
      <ErrorNote text={error?.message ?? years.error?.message} />
      {missing ? <p className="muted">Choose {has("ledger") || has("bank") ? "the account" : has("variant") ? "the item" : has("registration") ? "the GSTIN" : "the period"} to see the report.</p>
        : loading && !data ? <Loading />
        : data && data.length === 0 ? <p className="muted">Nothing to show for this period.</p>
        : data && <div ref={body} className="report-body"><p className="pdf-only"><b>{company.name}</b> · {title}{when ? ` · ${when}` : ""}</p>
                   {def.layout === "pl" ? <PL rows={data} ie={!!company.income_expenditure && !asPl} groups={ref?.groups ?? []} /> : def.layout === "bs" ? <BS rows={data} /> : def.layout === "schedule3" ? <Schedule3 rows={data} />
                   : def.layout === "rp" ? <RP rows={data} />
                   : <Table def={def} rows={data} onOpen={def.drill ? open : undefined} />}</div>}
      <p className="print-only muted small">{company.name} · {title} · {params.from ? `${fmtDate(params.from)} to ${fmtDate(params.to)}` : params.as_on ? `as at ${fmtDate(params.as_on)}` : ""}</p>
    </section>
  );
}

function usePicks(kind: "ledgers" | "banks" | "variants" | "warehouses" | "registrations" | null) {
  const { company } = useCompany();
  const [list, setList] = useState<Pick[]>([]);
  useEffect(() => { if (kind) pickApi.list(company.id, kind).then(setList).catch(() => setList([])); }, [company.id, kind]);
  return list;
}

function Params({ def, params, set, presets: ps }: { def: ReportDef; params: Record<string, string>; set: (p: Record<string, string | null>) => void;
  presets: { key: string; label: string; from: string; to: string }[] }) {
  const has = (p: Param) => def.params.includes(p);
  const ledgers = usePicks(has("ledger") ? "ledgers" : has("bank") ? "banks" : null);
  const variants = usePicks(has("variant") ? "variants" : null);
  const warehouses = usePicks(has("warehouse") ? "warehouses" : null);
  const regs = usePicks(has("registration") ? "registrations" : null);
  const current = ps.find((p) => p.from === params.from && p.to === params.to)?.key ?? "custom";
  const items: ReactNode[] = [];
  if (has("ledger") || has("bank")) items.push(<label key="l" className="wide-field">{has("bank") ? "Bank" : "Account"}<Combobox options={ledgers} value={params.ledger ?? null} autoFocus={!params.ledger} onChange={(v) => set({ ledger: v })} /></label>);
  if (has("variant")) items.push(<label key="v" className="wide-field">Item<Combobox options={variants} value={params.variant ?? null} onChange={(v) => set({ variant: v })} /></label>);
  if (has("registration") && regs.length > 1) items.push(<label key="g">GSTIN<select value={params.registration ?? ""} onChange={(e) => set({ registration: e.target.value })}>{regs.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></label>);
  if (has("range")) items.push(
    <label key="p">Period<select value={current} onChange={(e) => { const p = ps.find((x) => x.key === e.target.value); if (p) set({ from: p.from, to: p.to }); }}>
      {ps.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}<option value="custom">Custom</option></select></label>,
    <label key="f">From<input type="date" value={params.from ?? ""} onChange={(e) => set({ from: e.target.value })} /></label>,
    <label key="t">To<input type="date" value={params.to ?? ""} onChange={(e) => set({ to: e.target.value })} /></label>);
  if (has("as_on")) items.push(<label key="a">As at<input type="date" value={params.as_on ?? ""} onChange={(e) => set({ as_on: e.target.value })} /></label>);
  if (has("period")) items.push(<label key="m">Return period<input type="month" value={(params.period ?? "").slice(0, 7)} onChange={(e) => set({ period: e.target.value ? `${e.target.value}-01` : null })} /></label>);
  if (has("tax_year") || has("statement")) items.push(<label key="y">Tax year<select value={params.tax_year} onChange={(e) => set({ tax_year: e.target.value })}>
    {Array.from({ length: 4 }, (_, i) => taxYearOf(iso(new Date())) - i).filter((y) => y >= 2026).map((y) => <option key={y} value={y}>{y}-{String((y + 1) % 100).padStart(2, "0")}</option>)}</select></label>);
  if (has("statement")) items.push(
    <label key="fm">Form<select value={params.form} onChange={(e) => set({ form: e.target.value })}><option value="140">140 (TDS, residents)</option><option value="144">144 (TDS, non-residents)</option><option value="143">143 (TCS)</option></select></label>,
    <label key="q">Quarter<select value={params.quarter} onChange={(e) => set({ quarter: e.target.value })}>{[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}</select></label>);
  if (has("basis")) items.push(<label key="b">By<select value={params.basis} onChange={(e) => set({ basis: e.target.value })}><option value="due">Due date</option><option value="bill">Bill date</option></select></label>);
  if (has("warehouse") && warehouses.length > 0) items.push(<label key="w">Warehouse<select value={params.warehouse ?? ""} onChange={(e) => set({ warehouse: e.target.value || null })}>
    <option value="">All</option>{warehouses.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}</select></label>);
  return items.length ? <div className="toolbar params">{items}</div> : null;
}

export function Table({ def, rows, onOpen }: { def: { columns: Col[] }; rows: Row[]; onOpen?: (r: Row) => void }) {
  const [sel, setSel] = useState(0);
  const cols = def.columns;
  const totals = cols.map((c) => (c.total ? rows.reduce((t, r) => t + (Number(r[c.key]) || 0), 0) : null));
  return (
    <div className="table-wrap" tabIndex={0} aria-label="Report rows"
      onKeyDown={(e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, rows.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
        else if (e.key === "Enter" && onOpen && rows[sel]) { e.preventDefault(); onOpen(rows[sel]); }
      }}>
      <table className="grid-table report-table">
        <thead><tr>{cols.map((c) => <th key={c.key} className={numeric(c) ? "num" : ""}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className={`${onOpen ? "link-row" : ""} ${i === sel ? "sel" : ""}`} onClick={onOpen ? () => onOpen(r) : undefined}>
            {cols.map((c) => <td key={c.key} className={numeric(c) ? "num" : ""}>{cell(c, r[c.key])}</td>)}
          </tr>))}</tbody>
        {totals.some((t) => t != null) && <tfoot><tr>{cols.map((c, i) => <td key={c.key} className={numeric(c) ? "num" : ""}>{i === 0 ? "Total" : totals[i] != null ? cell(c, totals[i]) : ""}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
}

// the statements a trust can also run as a Profit and Loss, and the words that change back
const PL_TITLES: Record<string, string> = {
  profit_loss: "Profit and Loss", schedule3_pl: "Profit and Loss · Schedule III", profit_loss_compare: "Profit and Loss with Last Year",
};
const PL_WORDS: Record<string, string> = {
  "Excess of income over expenditure": "Net profit", "Gross surplus": "Gross profit",
  "Surplus before exceptional and extraordinary items and tax": "Profit before exceptional and extraordinary items and tax",
  "Surplus before tax": "Profit before tax", "Surplus for the period": "Profit for the period",
};

// Profit & loss: the trading part (to gross profit), then the rest (to net profit). Kept as an Income and Expenditure
// Account (a trust, a society), the result reads as an excess of one over the other.
function PL({ rows, ie, groups }: { rows: Row[]; ie: boolean; groups: Group[] }) {
  if (ie) return <IE rows={rows} groups={groups} />;
  const part = (p: string) => rows.filter((r) => r.part === p);
  const label = (r: Row, n: number) => {
    const l = String(r.label);
    if (r.line_type === "group" || n >= 0) return l;
    if (!ie) return `${l} (loss)`;
    return r.line_type === "net_profit" ? "Excess of expenditure over income" : r.line_type === "gross_profit" ? "Gross deficit" : l;
  };
  const line = (r: Row, i: number) => {
    const total = r.line_type !== "group"; const n = Number(r.amount);
    return <tr key={i} className={total ? "total-row" : ""}><td>{label(r, n)}</td><td className="num">{fmtAmount(total ? Math.abs(n) : n)}</td></tr>;
  };
  return (
    <table className="grid-table statement">
      <tbody>
        <tr className="band"><td colSpan={2}>Trading</td></tr>{part("trading").map(line)}
        <tr className="band"><td colSpan={2}>{ie ? "Income and expenditure" : "Profit and loss"}</td></tr>{part("profit_and_loss").map(line)}
      </tbody>
    </table>
  );
}

// An Income and Expenditure Account: income, then expenditure, then the excess of one over the other. Empty lines are
// left out; a trust that also trades (a canteen, a bookshop) keeps the trading part to its gross surplus.
function IE({ rows, groups }: { rows: Row[]; groups: Group[] }) {
  const nature = (r: Row) => groups.find((g) => g.parent_id == null && (r.root_code ? g.code === r.root_code : g.name === r.label))?.nature;
  const shown = rows.filter((r) => r.line_type !== "group" || Number(r.amount) !== 0);
  const trades = shown.some((r) => r.part === "trading" && r.line_type === "group" && nature(r) === "E");
  const net = rows.find((r) => r.line_type === "net_profit"), gross = rows.find((r) => r.line_type === "gross_profit");
  const lines = (rs: Row[]) => rs.map((r, i) => <tr key={i}><td>{String(r.label)}</td><td className="num">{fmtAmount(Number(r.amount))}</td></tr>);
  const result = (r: Row | undefined, plus: string, minus: string) => {
    if (!r) return null;
    const n = Number(r.amount);
    return <tr className="total-row"><td>{n < 0 ? minus : plus}</td><td className="num">{fmtAmount(Math.abs(n))}</td></tr>;
  };
  const sum = (rs: Row[]) => rs.reduce((t, r) => t + Number(r.amount), 0);
  const of = (part: string | null, n: "I" | "E") => shown.filter((r) => r.line_type === "group" && (part == null || r.part === part) && nature(r) === n);
  if (trades) {
    return (
      <table className="grid-table statement">
        <tbody>
          <tr className="band"><td colSpan={2}>Trading</td></tr>{lines(shown.filter((r) => r.part === "trading" && r.line_type === "group"))}
          {result(gross, String(gross?.label ?? "Gross surplus"), "Gross deficit")}
          <tr className="band"><td colSpan={2}>Income</td></tr>{lines(of("profit_and_loss", "I"))}
          <tr className="band"><td colSpan={2}>Expenditure</td></tr>{lines(of("profit_and_loss", "E"))}
          {result(net, String(net?.label ?? "Excess of income over expenditure"), "Excess of expenditure over income")}
        </tbody>
      </table>
    );
  }
  const inc = of(null, "I"), exp = of(null, "E");
  return (
    <table className="grid-table statement">
      <tbody>
        <tr className="band"><td colSpan={2}>Income</td></tr>{lines(inc)}
        <tr className="total-row"><td>Total income</td><td className="num">{fmtAmount(sum(inc))}</td></tr>
        <tr className="band"><td colSpan={2}>Expenditure</td></tr>{lines(exp)}
        <tr className="total-row"><td>Total expenditure</td><td className="num">{fmtAmount(sum(exp))}</td></tr>
        {result(net, String(net?.label ?? "Excess of income over expenditure"), "Excess of expenditure over income")}
      </tbody>
    </table>
  );
}

// Receipts and payments: opening cash and bank with what came in, against what went out with closing cash and bank;
// each side's accounts under their group. The two totals agree.
function RP({ rows }: { rows: Row[] }) {
  const side = (s: string) => rows.filter((r) => r.side === s);
  const sum = (s: string) => side(s).reduce((t, r) => t + Number(r.amount), 0);
  const body = (s: string) => {
    let group = "";
    return side(s).map((r, i) => {
      const g = r.line_type === "line" ? String(r.group_name ?? "") : "";
      const head = g && g !== group ? <tr key={`g${i}`} className="sub-band"><td colSpan={2}>{g}</td></tr> : null;
      if (r.line_type === "line") group = g;
      return [head, <tr key={i}><td className={r.line_type === "line" ? "indent" : ""}>{String(r.label)}</td><td className="num">{fmtAmount(Number(r.amount))}</td></tr>];
    });
  };
  const diff = Math.round((sum("receipts") - sum("payments")) * 100) / 100;
  return (
    <table className="grid-table statement">
      <tbody>
        <tr className="band"><td colSpan={2}>Receipts</td></tr>{body("receipts")}
        <tr className="total-row"><td>Total</td><td className="num">{fmtAmount(sum("receipts"))}</td></tr>
        <tr className="band"><td colSpan={2}>Payments</td></tr>{body("payments")}
        <tr className="total-row"><td>Total</td><td className="num">{fmtAmount(sum("payments"))}</td></tr>
        {diff !== 0 && <tr className="warn"><td>Difference</td><td className="num">{fmtAmount(diff)}</td></tr>}
      </tbody>
    </table>
  );
}

// Balance sheet: liabilities, then assets, each totalled; a difference shows if they don't agree
function BS({ rows }: { rows: Row[] }) {
  const side = (s: string) => rows.filter((r) => r.side === s);
  const sum = (s: string) => side(s).reduce((t, r) => t + Number(r.amount), 0);
  const diff = Math.round((sum("liabilities") - sum("assets")) * 100) / 100;
  return (
    <table className="grid-table statement">
      <tbody>
        <tr className="band"><td colSpan={2}>Liabilities</td></tr>
        {side("liabilities").map((r, i) => <tr key={`l${i}`}><td>{String(r.label)}</td><td className="num">{fmtAmount(Number(r.amount))}</td></tr>)}
        <tr className="total-row"><td>Total</td><td className="num">{fmtAmount(sum("liabilities"))}</td></tr>
        <tr className="band"><td colSpan={2}>Assets</td></tr>
        {side("assets").map((r, i) => <tr key={`a${i}`}><td>{String(r.label)}</td><td className="num">{fmtAmount(Number(r.amount))}</td></tr>)}
        <tr className="total-row"><td>Total</td><td className="num">{fmtAmount(sum("assets"))}</td></tr>
        {diff !== 0 && <tr className="warn"><td>Difference</td><td className="num">{fmtAmount(diff)}</td></tr>}
      </tbody>
    </table>
  );
}

// Schedule III (Division I): lines, section totals and totals as the core lays them out
function Schedule3({ rows }: { rows: Row[] }) {
  let lastPart = "";
  return (
    <table className="grid-table statement">
      <tbody>{rows.map((r, i) => {
        const head = r.part && r.part !== lastPart ? <tr key={`p${i}`} className="band"><td colSpan={2}>{String(r.part).replace(/_/g, " ")}</td></tr> : null;
        lastPart = String(r.part ?? lastPart);
        const t = String(r.row_type);
        return [head, <tr key={i} className={t === "total" || t === "section_total" ? "total-row" : t === "heading" ? "band" : ""}>
          <td className={t === "line" ? "indent" : ""}>{String(r.label)}</td><td className="num">{r.amount == null ? "" : fmtAmount(Number(r.amount))}</td></tr>];
      })}</tbody>
    </table>
  );
}
